/**
 * Relief and Rebuilding Home Status: derived from workscope statuses,
 * using the same definitions as the Reports page (reliefWorkScopes and rebuild blacklist).
 * Same priority and color logic as Goodwill Home Status.
 */

export type WorkscopeDisplayStatus =
  | "Not Started"
  | "In Progress"
  | "Complete"
  | "No Assistance Required"
  | "Unknown"

export type ReliefRebuildingStatusResult = {
  displayLabel: WorkscopeDisplayStatus
  colorClass: string
}

type StatusClaimedByPath = {
  statusPath: string
  claimedByPath?: string
}

// Relief work scopes (from Reports page reliefWorkScopes, excluding assess-needs so Relief Status
// reflects actual relief work only; assessment "Complete" would otherwise force Relief to Complete when all work is No Need)
const RELIEF_STATUS_PATHS: StatusClaimedByPath[] = [
  {
    statusPath: "home.treeDebrisInfo.heavyEquipmentNeeded",
    claimedByPath: "home.treeDebrisInfo.heavyClaimedBy",
  },
  {
    statusPath: "home.treeDebrisInfo.treeWorkNeeded",
    claimedByPath: "home.treeDebrisInfo.treeClaimedBy",
  },
  {
    statusPath: "home.treeDebrisInfo.exteriorDebrisRemovalNeeded",
    claimedByPath: "home.treeDebrisInfo.debrisClaimedBy",
  },
  {
    statusPath: "home.roofingInfo.roofTarpNeeded",
    claimedByPath: "home.roofingInfo.roofTarpClaimedBy",
  },
  {
    statusPath: "home.crawlSpaceInfo.crawlSpaceMuck",
    claimedByPath: "home.crawlSpaceInfo.crawlSpaceMuckClaimedBy",
  },
  {
    statusPath: "home.crawlSpaceInfo.crawlSpaceInsulation",
    claimedByPath: "home.crawlSpaceInfo.crawlSpaceInsulationClaimedBy",
  },
  {
    statusPath: "home.muckGutInfo.muckGutNeeded",
    claimedByPath: "home.muckGutInfo.claimedBy",
  },
  {
    statusPath: "home.moldRemediationInfo.moldRemediation",
    claimedByPath: "home.moldRemediationInfo.claimedBy",
  },
]

// Rebuilding work scopes = all status paths NOT in Relief blacklist.
// Assessment excluded. Used for "worst case" fallback only; Rebuilding uses order-based list below.
const REBUILDING_STATUS_PATHS: string[] = [
  "home.roofingInfo.roofing",
  "home.rebuildPrepInfo.rebuildPrep",
  "home.roughCarpentryInfo.roughCarpentry",
  "home.electricalInfo.electrical",
  "home.electricalInfo.electricalRoughIn",
  "home.hvacInfo.hvac",
  "home.hvacInfo.mechanicalRoughIn",
  "home.plumbingInfo.plumbing",
  "home.plumbingInfo.plumbingRoughIn",
  "home.insulationInstallInfo.insulationInstall",
  "home.drywallHangingInfo.drywallHanging",
  "home.mudAndTapeInfo.mudAndTape",
  "home.blockWallInfo.blockWallRepair",
  "home.stuccoInfo.stucco",
  "home.bathroomRebuildInfo.bathroomRebuild",
  "home.cabinetInstallInfo.cabinetInstall",
  "home.cabinetInstallInfo.counterInstall",
  "home.cabinetInstallInfo.applianceInstall",
  "home.interiorPaintInfo.interiorPaint",
  "home.flooringInstallInfo.flooringInstall",
  "home.installInteriorDoorsInfo.installInteriorDoors",
  "home.trimInstallInfo.trimInstall",
  "home.exteriorDoorInstallInfo.exteriorDoorInstall",
  "home.drivewayRepair.drivewayRepair",
  "home.stormShutters.shutterInstall",
]

// Order of Rebuilding workscopes as on the Home Details page (starting with Rebuild Prep).
// Rebuilding Status = first step in this order that is not "done" (Complete / No Assistance Required).
// Blank is treated as Not Started.
const REBUILDING_ORDERED_PATHS: StatusClaimedByPath[] = [
  {
    statusPath: "home.rebuildPrepInfo.rebuildPrep",
    claimedByPath: "home.rebuildPrepInfo.claimedBy",
  },
  {
    statusPath: "home.roughCarpentryInfo.roughCarpentry",
    claimedByPath: "home.roughCarpentryInfo.claimedBy",
  },
  {
    statusPath: "home.electricalInfo.electrical",
    claimedByPath: "home.electricalInfo.claimedBy",
  },
  {
    statusPath: "home.electricalInfo.electricalRoughIn",
    claimedByPath: "home.electricalInfo.electricalRoughInClaimedBy",
  },
  {
    statusPath: "home.hvacInfo.hvac",
    claimedByPath: "home.hvacInfo.claimedBy",
  },
  {
    statusPath: "home.hvacInfo.mechanicalRoughIn",
    claimedByPath: "home.hvacInfo.mechanicalRoughInClaimedBy",
  },
  {
    statusPath: "home.plumbingInfo.plumbing",
    claimedByPath: "home.plumbingInfo.claimedBy",
  },
  {
    statusPath: "home.plumbingInfo.plumbingRoughIn",
    claimedByPath: "home.plumbingInfo.plumbingRoughInClaimedBy",
  },
  {
    statusPath: "home.insulationInstallInfo.insulationInstall",
    claimedByPath: "home.insulationInstallInfo.claimedBy",
  },
  {
    statusPath: "home.drywallHangingInfo.drywallHanging",
    claimedByPath: "home.drywallHangingInfo.claimedBy",
  },
  {
    statusPath: "home.mudAndTapeInfo.mudAndTape",
    claimedByPath: "home.mudAndTapeInfo.claimedBy",
  },
  {
    statusPath: "home.blockWallInfo.blockWallRepair",
    claimedByPath: "home.blockWallInfo.claimedBy",
  },
  {
    statusPath: "home.stuccoInfo.stucco",
    claimedByPath: "home.stuccoInfo.claimedBy",
  },
  {
    statusPath: "home.bathroomRebuildInfo.bathroomRebuild",
    claimedByPath: "home.bathroomRebuildInfo.claimedBy",
  },
  {
    statusPath: "home.cabinetInstallInfo.cabinetInstall",
    claimedByPath: "home.cabinetInstallInfo.cabinetClaimedBy",
  },
  {
    statusPath: "home.cabinetInstallInfo.counterInstall",
    claimedByPath: "home.cabinetInstallInfo.counterClaimedBy",
  },
  {
    statusPath: "home.cabinetInstallInfo.applianceInstall",
    claimedByPath: "home.cabinetInstallInfo.applianceClaimedBy",
  },
  {
    statusPath: "home.interiorPaintInfo.interiorPaint",
    claimedByPath: "home.interiorPaintInfo.claimedBy",
  },
  {
    statusPath: "home.flooringInstallInfo.flooringInstall",
    claimedByPath: "home.flooringInstallInfo.claimedBy",
  },
  {
    statusPath: "home.installInteriorDoorsInfo.installInteriorDoors",
    claimedByPath: "home.installInteriorDoorsInfo.claimedBy",
  },
  {
    statusPath: "home.trimInstallInfo.trimInstall",
    claimedByPath: "home.trimInstallInfo.claimedBy",
  },
  {
    statusPath: "home.exteriorDoorInstallInfo.exteriorDoorInstall",
    claimedByPath: "home.exteriorDoorInstallInfo.claimedBy",
  },
  {
    statusPath: "home.drivewayRepair.drivewayRepair",
    claimedByPath: "home.drivewayRepair.drivewayClaimedBy",
  },
  {
    statusPath: "home.stormShutters.shutterInstall",
    claimedByPath: "home.stormShutters.shuttersClaimedBy",
  },
]

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce((current: unknown, key) => (current as Record<string, unknown>)?.[key], obj)
}

// Same priority/color mapping as Goodwill Home Status
function mapRawToDisplay(raw: string | null | undefined): { priority: number; result: ReliefRebuildingStatusResult } | null {
  if (!raw || typeof raw !== "string") return null
  const s = raw.trim()
  if (!s) return null

  if (s === "Needed Not Started") {
    return { priority: 1, result: { displayLabel: "Not Started", colorClass: "bg-gray-100 border-gray-300 text-gray-800" } }
  }
  if (["75%", "50%", "25%", "Materials Only"].includes(s)) {
    return { priority: 2, result: { displayLabel: "In Progress", colorClass: "bg-yellow-100 border-yellow-300 text-yellow-800" } }
  }
  if (s === "Unknown") {
    return { priority: 3, result: { displayLabel: "Unknown", colorClass: "bg-gray-100 border-gray-300 text-gray-800" } }
  }
  if (["Complete", "Materials Only Complete"].includes(s)) {
    return { priority: 4, result: { displayLabel: "Complete", colorClass: "bg-green-100 border-green-300 text-green-800" } }
  }
  if (s === "No Assistance Required") {
    return { priority: 5, result: { displayLabel: "No Assistance Required", colorClass: "bg-green-100 border-green-300 text-green-800" } }
  }
  return null
}

// When no statuses are set (all blank), show Not Started for Relief and Rebuilding
const BLANK_DEFAULT_RESULT: ReliefRebuildingStatusResult = {
  displayLabel: "Not Started",
  colorClass: "bg-gray-100 border-gray-300 text-gray-800",
}

const COMPLETE_RESULT: ReliefRebuildingStatusResult = {
  displayLabel: "Complete",
  colorClass: "bg-green-100 border-green-300 text-green-800",
}

const NO_ASSISTANCE_RESULT: ReliefRebuildingStatusResult = {
  displayLabel: "No Assistance Required",
  colorClass: "bg-green-100 border-green-300 text-green-800",
}

function claimedByIncludesHomeowner(raw: unknown): boolean {
  if (raw == null) return false
  if (Array.isArray(raw)) return raw.some(claimedByIncludesHomeowner)
  if (typeof raw !== "string") return false

  const value = raw.trim()
  if (!value) return false
  if (value === "Homeowner" || value === "Homeowner Scopes") return true

  try {
    return claimedByIncludesHomeowner(JSON.parse(value))
  } catch {
    return false
  }
}

function computeFromPaths(formValues: unknown, paths: StatusClaimedByPath[]): ReliefRebuildingStatusResult {
  let best: { priority: number; result: ReliefRebuildingStatusResult } | null = null
  let anyStatusFound = false
  let anyIncludedStatusFound = false

  for (const { statusPath, claimedByPath } of paths) {
    const status = getNestedValue(formValues, statusPath) as string | null | undefined
    const mapped = mapRawToDisplay(status)
    if (!mapped) continue
    anyStatusFound = true
    if (claimedByIncludesHomeowner(claimedByPath ? getNestedValue(formValues, claimedByPath) : null)) {
      continue
    }
    anyIncludedStatusFound = true
    if (best === null || mapped.priority < best.priority) {
      best = mapped
    }
  }
  if (best) return best.result
  if (anyStatusFound && !anyIncludedStatusFound) return NO_ASSISTANCE_RESULT
  return BLANK_DEFAULT_RESULT
}

/**
 * Rebuilding Status by process order: first step in order that is not "done" (Complete / No Assistance Required).
 * Blank/missing paths are skipped (inactive work scopes or N/A for this home). Assessment is excluded (not in REBUILDING_ORDERED_PATHS).
 * If all steps that have a value are "done": all No Assistance Required → No Assistance Required; any Complete → Complete.
 * If no steps have a value, returns Not Started.
 */
function computeRebuildingStatusByOrder(formValues: unknown): ReliefRebuildingStatusResult {
  let allStepsNoAssistance = true
  let anyStatusFound = false
  let anyIncludedStatusFound = false
  for (const { statusPath, claimedByPath } of REBUILDING_ORDERED_PATHS) {
    const status = getNestedValue(formValues, statusPath) as string | null | undefined
    const mapped = mapRawToDisplay(status)
    if (!mapped) continue // skip blank or missing (inactive scope / N/A)
    anyStatusFound = true
    if (claimedByIncludesHomeowner(claimedByPath ? getNestedValue(formValues, claimedByPath) : null)) {
      continue
    }
    anyIncludedStatusFound = true
    if (mapped.result.displayLabel === "Complete" || mapped.result.displayLabel === "No Assistance Required") {
      if (mapped.result.displayLabel === "Complete") allStepsNoAssistance = false
      continue
    }
    return mapped.result
  }
  if (!anyStatusFound) return BLANK_DEFAULT_RESULT
  if (!anyIncludedStatusFound) return NO_ASSISTANCE_RESULT
  return allStepsNoAssistance ? NO_ASSISTANCE_RESULT : COMPLETE_RESULT
}

/**
 * Compute Relief Home Status from form values (Relief workscopes from Reports page).
 */
export function computeReliefStatus(formValues: unknown): ReliefRebuildingStatusResult {
  return computeFromPaths(formValues, RELIEF_STATUS_PATHS)
}

/**
 * Compute Rebuilding Home Status from form values. Uses process order (as on Home Details page starting with Rebuild Prep):
 * first step that is not Complete or No Assistance Required determines the status.
 */
export function computeRebuildingStatus(formValues: unknown): ReliefRebuildingStatusResult {
  return computeRebuildingStatusByOrder(formValues)
}
